import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Grid,
  Card,
  CardContent,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Tooltip,
  TablePagination,
  InputAdornment
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Search as SearchIcon,
  Download as DownloadIcon,
  Business as BusinessIcon,
  Factory as FactoryIcon,
  LocationOn as LocationIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

const SupplierMaster = () => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentRecord, setCurrentRecord] = useState({
    mill_code: '',
    supplier_code: '',
    supplier_name: '',
    full_name: '',
    address1: '',
    address2: '',
    address3: '',
    address4: '',
    supplier_type: 'M'
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const { api } = useAuth();

  useEffect(() => {
    fetchRecords();
  }, []);

  const fetchRecords = async () => {
    try {
      setLoading(true);
      const response = await api.get('/masters/suppliers');
      setRecords(response.data.data || []);
    } catch (error) {
      console.error('Error fetching supplier records:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setCurrentRecord({
      mill_code: '',
      supplier_code: '',
      supplier_name: '',
      full_name: '',
      address1: '',
      address2: '',
      address3: '',
      address4: '',
      supplier_type: 'M'
    });
    setEditMode(false);
    setOpenDialog(true);
  };

  const handleEdit = (record) => {
    setCurrentRecord(record);
    setEditMode(true);
    setOpenDialog(true);
  };

  const handleView = (record) => {
    setCurrentRecord(record);
    setOpenViewDialog(true);
  };

  const handleSave = async () => {
    try {
      if (editMode) {
        await api.put(`/masters/suppliers/${currentRecord.supplier_code}`, currentRecord);
      } else {
        await api.post('/masters/suppliers', currentRecord);
      }
      fetchRecords();
      setOpenDialog(false);
    } catch (error) {
      console.error('Error saving supplier record:', error);
    }
  };

  const handleExport = () => {
    const csvContent = [
      ['Mill Code', 'Supplier Code', 'Supplier Name', 'Full Name', 'Type', 'Address'],
      ...filteredRecords.map(record => [
        record.mill_code,
        record.supplier_code,
        record.supplier_name,
        record.full_name || '',
        record.supplier_type,
        [record.address1, record.address2, record.address3, record.address4]
          .filter(Boolean).join(', ')
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'supplier_master.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const getSupplierTypeText = (type) => {
    switch (type) {
      case 'M': return 'Mill';
      case 'S': return 'Supplier';
      case 'V': return 'Vendor';
      default: return type;
    }
  };

  const getSupplierTypeColor = (type) => {
    switch (type) {
      case 'M': return 'primary';
      case 'S': return 'success';
      case 'V': return 'warning';
      default: return 'default';
    }
  };

  const filteredRecords = records.filter(record =>
    record.mill_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    record.supplier_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    record.supplier_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    record.full_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const paginatedRecords = filteredRecords.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <BusinessIcon sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h4" gutterBottom color="primary">
            Supplier Master
          </Typography>
        </Box>
        <Typography variant="body1" color="textSecondary">
          Steel suppliers and mill information management
        </Typography>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Suppliers
              </Typography>
              <Typography variant="h4">
                {records.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Mills
              </Typography>
              <Typography variant="h4" color="primary.main">
                {records.filter(r => r.supplier_type === 'M').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Suppliers
              </Typography>
              <Typography variant="h4" color="success.main">
                {records.filter(r => r.supplier_type === 'S').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Search Results
              </Typography>
              <Typography variant="h4" color="warning.main">
                {filteredRecords.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Search and Actions */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
        <TextField
          label="Search suppliers..."
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            )
          }}
          sx={{ flexGrow: 1, maxWidth: 400 }}
        />
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={handleExport}
        >
          Export CSV
        </Button>
      </Box>

      {/* Data Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Mill Code</strong></TableCell>
                <TableCell><strong>Supplier Code</strong></TableCell>
                <TableCell><strong>Supplier Name</strong></TableCell>
                <TableCell><strong>Full Name</strong></TableCell>
                <TableCell><strong>Type</strong></TableCell>
                <TableCell><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedRecords.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography variant="body1" color="textSecondary">
                      No supplier records found. Add suppliers to get started.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedRecords.map((record) => (
                  <TableRow key={record.supplier_code} hover>
                    <TableCell>
                      <Chip 
                        label={record.mill_code} 
                        color="secondary" 
                        variant="outlined" 
                        size="small"
                        icon={<FactoryIcon />}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                        {record.supplier_code}
                      </Typography>
                    </TableCell>
                    <TableCell>{record.supplier_name}</TableCell>
                    <TableCell>{record.full_name || '-'}</TableCell>
                    <TableCell>
                      <Chip
                        label={getSupplierTypeText(record.supplier_type)}
                        color={getSupplierTypeColor(record.supplier_type)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Tooltip title="View Details">
                        <IconButton size="small" onClick={() => handleView(record)}>
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => handleEdit(record)}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={filteredRecords.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(event, newPage) => setPage(newPage)}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
        />
      </Paper>

      {/* Floating Action Button */}
      <Fab
        color="primary"
        aria-label="add"
        sx={{ position: 'fixed', bottom: 16, right: 16 }}
        onClick={handleAdd}
      >
        <AddIcon />
      </Fab>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editMode ? 'Edit Supplier' : 'Add New Supplier'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Mill Code"
                value={currentRecord.mill_code}
                onChange={(e) => setCurrentRecord({...currentRecord, mill_code: e.target.value})}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Supplier Code"
                value={currentRecord.supplier_code}
                onChange={(e) => setCurrentRecord({...currentRecord, supplier_code: e.target.value})}
                disabled={editMode}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Supplier Name"
                value={currentRecord.supplier_name}
                onChange={(e) => setCurrentRecord({...currentRecord, supplier_name: e.target.value})}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Full Name"
                value={currentRecord.full_name}
                onChange={(e) => setCurrentRecord({...currentRecord, full_name: e.target.value})}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address Line 1"
                value={currentRecord.address1}
                onChange={(e) => setCurrentRecord({...currentRecord, address1: e.target.value})}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address Line 2"
                value={currentRecord.address2}
                onChange={(e) => setCurrentRecord({...currentRecord, address2: e.target.value})}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address Line 3"
                value={currentRecord.address3}
                onChange={(e) => setCurrentRecord({...currentRecord, address3: e.target.value})}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address Line 4"
                value={currentRecord.address4}
                onChange={(e) => setCurrentRecord({...currentRecord, address4: e.target.value})}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Supplier Type</InputLabel>
                <Select
                  value={currentRecord.supplier_type}
                  label="Supplier Type"
                  onChange={(e) => setCurrentRecord({...currentRecord, supplier_type: e.target.value})}
                >
                  <MenuItem value="M">Mill</MenuItem>
                  <MenuItem value="S">Supplier</MenuItem>
                  <MenuItem value="V">Vendor</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">
            {editMode ? 'Update' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={openViewDialog} onClose={() => setOpenViewDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BusinessIcon />
            Supplier Details
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" gutterBottom color="primary">
              {currentRecord.supplier_name}
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="caption" color="textSecondary">Mill Code</Typography>
                <Typography variant="body2">{currentRecord.mill_code}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="textSecondary">Supplier Code</Typography>
                <Typography variant="body2">{currentRecord.supplier_code}</Typography>
              </Grid>
              
              <Grid item xs={12}>
                <Typography variant="caption" color="textSecondary">Full Name</Typography>
                <Typography variant="body2">{currentRecord.full_name || '-'}</Typography>
              </Grid>
              
              <Grid item xs={6}>
                <Typography variant="caption" color="textSecondary">Type</Typography>
                <Box sx={{ mt: 0.5 }}>
                  <Chip
                    label={getSupplierTypeText(currentRecord.supplier_type)}
                    color={getSupplierTypeColor(currentRecord.supplier_type)}
                    size="small"
                  />
                </Box>
              </Grid>
              
              {(currentRecord.address1 || currentRecord.address2 || 
                currentRecord.address3 || currentRecord.address4) && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="textSecondary">Address</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mt: 0.5 }}>
                    <LocationIcon sx={{ fontSize: 16, color: 'text.secondary', mt: 0.2 }} />
                    <Box>
                      {currentRecord.address1 && (
                        <Typography variant="body2">{currentRecord.address1}</Typography>
                      )}
                      {currentRecord.address2 && (
                        <Typography variant="body2">{currentRecord.address2}</Typography>
                      )}
                      {currentRecord.address3 && (
                        <Typography variant="body2">{currentRecord.address3}</Typography>
                      )}
                      {currentRecord.address4 && (
                        <Typography variant="body2">{currentRecord.address4}</Typography>
                      )}
                    </Box>
                  </Box>
                </Grid>
              )}
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenViewDialog(false)}>Close</Button>
          <Button 
            onClick={() => {
              setOpenViewDialog(false);
              handleEdit(currentRecord);
            }} 
            variant="contained"
          >
            Edit
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SupplierMaster;